import { useMemo, useState } from 'react';

import { EMedicalCategoryType } from '@/entities/medical-category';

export const BottomSheetType = {
  MATERNITY: 'maternity',
  MALE: 'male',
  MENTAL_HEALTH: 'mentalHealth',
  DIET: 'diet',
  SLEEP_DISORDER: 'sleepDisorder',
  NONE: null,
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type BottomSheetType = (typeof BottomSheetType)[keyof typeof BottomSheetType];

const MATERNITY_RELATED_TREATMENT_CATEGORY_IDS = ['584', '586'];
const MATERNITY_RELATED_DEPARTMENT_CATEGORY_IDS = ['10'];
const MALE_RELATED_TREATMENT_CATEGORY_IDS = ['606'];
const MALE_RELATED_DEPARTMENT_CATEGORY_IDS = ['9'];
const MENTAL_HEALTH_RELATED_TREATMENT_CATEGORY_IDS = ['8', '588', '589'];
const MENTAL_HEALTH_RELATED_DEPARTMENT_CATEGORY_IDS = ['7'];
const DIET_TREATMENT_CATEGORY_ID = '12';
const SLEEP_DISORDER_TREATMENT_CATEGORY_ID = '619';

interface CategoryGuideBottomSheetStates {
  isMaternityGuideBottomSheetOpen: boolean;
  isMaleGuideBottomSheetOpen: boolean;
  isMentalHealthGuideBottomSheetOpen: boolean;
  isDietGuideBottomSheetOpen: boolean;
  isSleepDisorderGuideBottomSheetOpen: boolean;
}

interface CategoryGuideBottomSheetActions {
  openMaternityGuideBottomSheet: () => void;
  closeMaternityGuideBottomSheet: () => void;
  openMaleGuideBottomSheet: () => void;
  closeMaleGuideBottomSheet: () => void;
  openMentalHealthGuideBottomSheet: () => void;
  closeMentalHealthGuideBottomSheet: () => void;
  openDietGuideBottomSheet: () => void;
  closeDietGuideBottomSheet: () => void;
  openSleepDisorderGuideBottomSheet: () => void;
  closeSleepDisorderGuideBottomSheet: () => void;
}

interface UseCategoryGuideBottomSheet {
  categoryGuideBottomSheetStates: CategoryGuideBottomSheetStates;
  categoryGuideBottomSheetActions: CategoryGuideBottomSheetActions;
  categoryGuideBottomSheetOpenActionMap: Record<string, () => void>;
}

const useCategoryGuideBottomSheet = (): UseCategoryGuideBottomSheet => {
  const [activeSheet, setActiveSheet] = useState<BottomSheetType>(BottomSheetType.NONE);

  const states = useMemo(
    () => ({
      isMaternityGuideBottomSheetOpen: activeSheet === BottomSheetType.MATERNITY,
      isMaleGuideBottomSheetOpen: activeSheet === BottomSheetType.MALE,
      isMentalHealthGuideBottomSheetOpen: activeSheet === BottomSheetType.MENTAL_HEALTH,
      isDietGuideBottomSheetOpen: activeSheet === BottomSheetType.DIET,
      isSleepDisorderGuideBottomSheetOpen: activeSheet === BottomSheetType.SLEEP_DISORDER,
    }),
    [activeSheet]
  );

  const actions = useMemo(() => {
    const closeAll = () => setActiveSheet(BottomSheetType.NONE);
    return {
      openMaternityGuideBottomSheet: () => setActiveSheet(BottomSheetType.MATERNITY),
      closeMaternityGuideBottomSheet: closeAll,
      openMaleGuideBottomSheet: () => setActiveSheet(BottomSheetType.MALE),
      closeMaleGuideBottomSheet: closeAll,
      openMentalHealthGuideBottomSheet: () => setActiveSheet(BottomSheetType.MENTAL_HEALTH),
      closeMentalHealthGuideBottomSheet: closeAll,
      openDietGuideBottomSheet: () => setActiveSheet(BottomSheetType.DIET),
      closeDietGuideBottomSheet: closeAll,
      openSleepDisorderGuideBottomSheet: () => setActiveSheet(BottomSheetType.SLEEP_DISORDER),
      closeSleepDisorderGuideBottomSheet: closeAll,
    };
  }, []);

  const openActionMap = useMemo(
    () => ({
      ...MATERNITY_RELATED_TREATMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({ ...acc, [`${id}:${EMedicalCategoryType.TREATMENT}`]: actions.openMaternityGuideBottomSheet }),
        {}
      ),
      ...MATERNITY_RELATED_DEPARTMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({ ...acc, [`${id}:${EMedicalCategoryType.DEPARTMENT}`]: actions.openMaternityGuideBottomSheet }),
        {}
      ),
      ...MALE_RELATED_TREATMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({ ...acc, [`${id}:${EMedicalCategoryType.TREATMENT}`]: actions.openMaleGuideBottomSheet }),
        {}
      ),
      ...MALE_RELATED_DEPARTMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({ ...acc, [`${id}:${EMedicalCategoryType.DEPARTMENT}`]: actions.openMaleGuideBottomSheet }),
        {}
      ),
      ...MENTAL_HEALTH_RELATED_TREATMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({
          ...acc,
          [`${id}:${EMedicalCategoryType.TREATMENT}`]: actions.openMentalHealthGuideBottomSheet,
        }),
        {}
      ),
      ...MENTAL_HEALTH_RELATED_DEPARTMENT_CATEGORY_IDS.reduce(
        (acc, id) => ({
          ...acc,
          [`${id}:${EMedicalCategoryType.DEPARTMENT}`]: actions.openMentalHealthGuideBottomSheet,
        }),
        {}
      ),
      [`${DIET_TREATMENT_CATEGORY_ID}:${EMedicalCategoryType.TREATMENT}`]: actions.openDietGuideBottomSheet,
      [`${SLEEP_DISORDER_TREATMENT_CATEGORY_ID}:${EMedicalCategoryType.TREATMENT}`]:
        actions.openSleepDisorderGuideBottomSheet,
    }),
    [actions]
  );

  return {
    categoryGuideBottomSheetStates: states,
    categoryGuideBottomSheetActions: actions,
    categoryGuideBottomSheetOpenActionMap: openActionMap,
  };
};

export default useCategoryGuideBottomSheet;
